<template>
	<div class="news-archive">
		<template :if="news.length > 0">

			<!-- CATEGORIES -->
			<div class="category-bar header--additive-padding">
				<div class="page-container">
					<div
						v-if="!loadingCategories"
						class="news-archive__categories"
						v-dragscroll
					>
						<router-link
							:to="{
								name: 'NewsArchive'
							}"
							class="news-archive__category-wrap"
							@click.native="categoryFilter = null"
						>
							<btn
								class="btn--style--custom news-archive__category"
								:class="{ 'news-archive__category--active': categoryFilter == null }"
							>All</btn>
						</router-link>
						<router-link
							v-for="category in categories"
							:key="category.id"
							:to="{
								name: 'NewsArchive',
								params: {
									category: category.id
								}
							}"
							class="news-archive__category-wrap"
							@click.native="categoryFilter = category.id"
						>
							<btn
								class="btn--style--custom news-archive__category"
								:class="{ 'news-archive__category--active': categoryFilter === category.id }"
							>{{ category.name }}</btn>
						</router-link>
					</div>
					<loader v-else />
				</div>
			</div>

			<template v-if="!loadingNews">

				<!-- LATEST -->
				<div
					v-if="filteredNews.length === 0"
					class="panel"
				>
					<div class="page-container">
						<p>No articles found.</p>
					</div>
				</div>

				<!-- LATEST -->
				<div
					v-if="filteredNews.length > 0 && compCurrentPage === 1"
					class="panel"
				>
					<div
						class="page-container"
						ref="containerStart"
					>
						<div class="panel__title">
							<h2>Latest {{ title }}</h2>
						</div>

						<div class="grid">
							<div
								v-for="item in paginatedNews.slice(0, 2)"
								:key="item.id"
								class="grid__item one-half"
							>
							<div class="latest_grid">
								<news-overview
									:news="item"
									:show-summary="false"
									:category="categories.find(category => category.id === item.category)"
									class="news-overview--latest"
								/>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- FEATURED -->
				<div
					v-if="featuredNews.length > 0 && compCurrentPage === 1"
					class="panel panel--dark"
					style="background: #333"
				>
					<div
						class="page-container"
						ref="containerStart"
					>
						<div class="panel__title">
							<h2>Featured {{ title }}</h2>
						</div>

						<div class="grid">
							<div
								v-for="item in featuredNews"
								:key="item.id"
								class="grid__item one-third--not-phab one-half--phab"
							>
							<div class="featured_grid">
								<news-overview
									:news="item"
									:category="categories.find(category => category.id === item.category)"
									class="news-overview--featured"
								/>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- ALL -->
				<div
					v-if="filteredNews.length > 0"
					class="panel"
				>
					<div
						class="page-container"
						ref="containerStart"
					>
						<div class="panel__title">
							<h2>All {{ title }} Articles</h2>
							<h3
								v-html="description"
								class="h4 fc-pal--grey"
							/>
						</div>

						<div class="grid">
							<div
								v-for="item in paginatedNews"
								:key="item.id"
								class="grid__item one-quarter one-half--phab"
							>
								<news-overview
									:news="item"
									:category="categories.find(category => category.id === item.category)"
									class="news-overview"
								/>
							</div>
						</div>

						<pagination-controls
							v-if="totalPages > 1"
							:current-page="compCurrentPage"
							:total-pages="totalPages"
							@page-change="onPageChange"
						/>
					</div>
				</div>

			</template>
			<loader v-else />

		</template>
	</div>
</template>

<script>
import { filter, orderBy, has, isEmpty } from 'lodash'

import Btn from './atoms/Btn'
import Banner from './molecules/Banner'
import NewsOverview from './molecules/NewsOverview'
import PaginationControls from './molecules/PaginationControls'
import Loader from './atoms/Loader'

export default {
	name: 'NewsArchive',
	components: {
		Loader,
		Btn,
		Banner,
		NewsOverview,
		PaginationControls
	},
	data () {
		return {
			heroData: null,
			news: [],
			currentPage: 1,
			postLimit: 12,
			categoryFilter: null,
			categories: [],
			loadingNews: false,
			loadingCategories: false
		}
	},
	computed: {
		bannerBackgroundImage () {
			if (has(this.heroData, 'desktopBackgroundImage')) {
				return {
					mob: this.heroData.mobileBackgroundImage.link,
					ptab: this.heroData.mobileBackgroundImage.link,
					ltab: this.heroData.desktopBackgroundImage.link,
					desk: this.heroData.desktopBackgroundImage.link,
				}
			}
			return null
		},
		bannerCta () {
			if (has(this.heroData, 'cta.0.url') && !isEmpty(this.heroData.cta[0].url) && !isEmpty(this.heroData.cta[0].text)) {
				return this.heroData.cta[0]
			}
			return null
		},
		title () {
			const categoryName = this.categoryFilter ? this.categories.find(category => category.id === this.categoryFilter).name : null
			switch (categoryName) {
				case 'Games':
					return 'Games News'
				case 'Events':
					return 'Events'
				case 'Industry':
					return 'Industry News'
				case 'Company':
					return 'Company News'
				default:
					return 'News'
			}
		},
		description () {
			return 'Stay current with the latest IGT news and events.'
		},
		filteredNews () {
			if (this.categoryFilter === null) return this.news
			return filter(this.news, news => {
				return news.category === this.categoryFilter
			})
		},
		paginatedNews () {
			const offset = (this.compCurrentPage - 1) * this.postLimit
			if (offset > this.filteredNews.length) return []
			return this.filteredNews.slice(offset, offset + this.postLimit)
		},
		featuredNews () {
			return this.filteredNews.filter(item => item.featured === true).slice(0, 3)
		},
		totalPages () {
			return Math.ceil(this.filteredNews.length / this.postLimit)
		},
		compCurrentPage () {
			return Math.max(1, Math.min(this.currentPage, this.totalPages))
		}
	},
	watch: {
		'$root.userSelectedRegionKey': async function () {
			this.$root.appImmediateLoading = true
			await this.getNewsPosts()
			this.$root.appImmediateLoading = false
		},
	},
	created () {
		this.getCategories()
		if (this.$route.params.hasOwnProperty('category')) {
			const categoryId = parseInt(this.$route.params.category)
			this.categoryFilter = !Number.isNaN(categoryId) ? categoryId : null
		} else {
			this.categoryFilter = null
		}

		this.getNewsPosts()
		if (this.$route.params.hasOwnProperty('pageNo')) {
			this.currentPage = parseInt(this.$route.params.pageNo)
		}
	},
	methods: {
		getNewsPosts () {
			const self = this
			this.loadingNews = true

			// Get all news posts
			const regions = this.$root.userSelectedRegion ? [this.$root.userSelectedRegion.key] : null
			return self.$api.getNews(null, null, null, null, null, regions)
				.then(data => {
					if (data.hasOwnProperty('data')) {
						self.sortNews(data)
					}
					this.loadingNews = false
					self.$root.$data.appLoading = false
				})
				.catch(error => {
					this.loadingNews = true
					self.$alert.danger({ message: error.message })
				})
		},
		getCategories () {
			const self = this
			this.loadingCategories = true
			self.$api.getCategories().then((data) => {
				this.loadingCategories = false
				if (data.hasOwnProperty('data') && Array.isArray(data.data)) {
					self.categories = data.data.sort((a, b) => a.order - b.order)
				}
			}).catch((error) => {
				this.loadingCategories = false
				self.$alert.danger({ message: error.message })
			})
		},
		sortNews (data) {
			const newsArray = orderBy(data.data.results, ['date'], ['desc'])
			this.news = newsArray.filter(
				x => x.status === true
			)
		},
		onPageChange (page) {
			this.currentPage = page
			this.$scrollTo(this.$refs.containerStart, 500, {
				offset: -80
			})
		}
	},
}
</script>

<style lang="scss">

@import "src/scss/toolkit";


.category-bar {
	padding-top: rem(48);
	padding-bottom: rem(48);
	border-bottom: 1px solid palette('divider');
}

.news-overview {

	.news-overview__title {
		font-size: rem(20);
		font-weight: bold;
		line-height: rem(32);
	}

	.news-overview__summary {
		font-size: rem(16);
		line-height: rem(26);
	}

	&--latest {

		.news-overview__title {
			font-size: rem(32);
			line-height: rem(40);
		}
	}

	&--featured {

		.news-overview__meta,
		.news-overview__title,
		.news-overview__summary {
			color: palette('white') !important;
		}
	}
}

$c: ".news-archive";
#{$c} {

	.page-container {
		max-width: rem(1280);
	}

	h2 {
		@include extend(h3);
		color: palette('black');
		font-weight: bold;
	}

	.panel--dark h2 {
		color: palette('white');
	}

	&__banner {
		min-height: 290px;
		@include media(not-phab) {
			min-height: 400px;
		}

		&.banner {
			@include extend(vertical-bottom);
			padding: {
				top: rem(40);
				bottom: rem(40);
			}
			@include media(not-phab) {
				padding: {
					top: rem(56);
					bottom: rem(56);
				}
			}
		}

		.banner__content {
			text-align: center;
		}

		&-btn {
			&[disabled] {
				background-color: palette(grey);
				opacity: 0.9;
			}
		}
	}

	&__categories {
		display: flex;
		justify-content: center;
	}

	&__category-wrap {
		padding-right: $page-padding;

		&:last-child {
			padding-right: 0;
		}
	}

	&__category {
		@include extend(btn);
		@include extend(btn--style--stroke);
		@include extend(btn--style--stroke--light-grey);
		padding-left: rem(24);
		padding-right: rem(24);
		color: palette('grey');
		background: transparent;
		border-width: 1px;

		&:after {
			height: 46px;
		}

		&--active {
			color: palette('font');
			border-color: currentColor;
			box-shadow: inset 0 0 0 1px palette('font');
		}
	}
}

.latest_grid .wistia_embed {
	height: 432px!important;
	object-fit: cover;
}
.featured_grid .wistia_embed{
height: 276px!important;
	object-fit: cover;
}
</style>



// WEBPACK FOOTER //
// src/components/NewsArchive.vue